import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const InviteRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company_name: '',
  });

  useEffect(() => {
    const validateInvite = async () => {
      if (!inviteToken) {
        setError('No invitation token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/invites/validate/${inviteToken}`);
        setInviteData(response.data);
        setFormData(prev => ({
          ...prev,
          name: response.data.name || '',
        }));
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/invites/accept', {
        token: inviteToken,
        name: formData.name,
        password: formData.password,
        phone: formData.phone,
        company_name: formData.company_name,
      });

      // Auto-login with the new credentials
      await login(inviteData.email, formData.password);
      toast.success('Account created successfully! Welcome to ConstructOne.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background bg-grid flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background bg-grid flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border rounded-sm">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Invalid Invitation</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <p className="text-sm text-muted-foreground">
                Please contact your administrator for a new invitation link.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-grid flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-card border-border rounded-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png" 
              alt="ConstructOne" 
              className="h-24 object-contain"
            />
          </div>
          <div className="flex items-center justify-center gap-2 text-green-500 mb-2">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Valid Invitation</span>
          </div>
          <CardDescription>
            Complete your registration to join {inviteData?.company_name || 'ConstructOne'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-secondary/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-muted-foreground">You've been invited as:</p>
            <p className="font-medium text-foreground">{inviteData?.email}</p>
            <p className="text-sm text-primary uppercase mt-1">{inviteData?.role}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                placeholder="John Smith"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-input border-border rounded-sm"
                data-testid="invite-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  placeholder="0400 000 000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-input border-border rounded-sm"
                  data-testid="invite-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company (Optional)</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  placeholder="Company name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="bg-input border-border rounded-sm"
                  data-testid="invite-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="bg-input border-border rounded-sm pr-10"
                    data-testid="invite-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="bg-input border-border rounded-sm"
                  data-testid="invite-confirm-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full uppercase font-bold tracking-wide rounded-sm"
              disabled={submitting}
              data-testid="invite-submit"
            >
              {submitting ? 'Creating Account...' : 'Complete Registration'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/psalm12" className="text-primary hover:underline">
              Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteRegisterPage;
